import React, { useCallback, useEffect, useState } from "react";
import { Card, Row, Col, Segmented, Typography, Spin, Empty } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from "@ant-design/icons";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import axiosInstance from "../../variables/api/axiosInstance";
import { COLORS } from "../../components/ui/colors";
import { displayNumbers } from "../../variables/numbersFunctions";

const { Text } = Typography;

const PERIODES = [
  { label: "Jour", value: "jour" },
  { label: "Semaine", value: "semaine" },
  { label: "Mois", value: "mois" },
  { label: "Exercice", value: "exercice" },
];

const PIE_COLORS = ["#1890ff", "#52c41a", "#722ed1", "#fa8c16", "#eb2f96", "#13c2c2", "#faad14", "#ff4d4f"];

const fmt = (v, format) => {
  if (format === "currency") return `${displayNumbers(Math.trunc(v || 0))} F`;
  if (format === "percent") return `${(v || 0).toFixed(0)}%`;
  return `${displayNumbers(Math.trunc(v || 0))}`;
};

const deltaPct = (value, previous) => {
  if (!previous || previous === 0) return value > 0 ? 100 : 0;
  return ((value - previous) / previous) * 100;
};

const Delta = ({ value, previous }) => {
  const d = deltaPct(value, previous);
  const flat = Math.abs(d) < 0.5;
  const up = d >= 0;
  const color = flat ? "#8c8c8c" : up ? "#52c41a" : "#ff4d4f";
  const Icon = flat ? MinusOutlined : up ? ArrowUpOutlined : ArrowDownOutlined;
  return (
    <span style={{ color, fontSize: 12, fontWeight: 600 }}>
      <Icon style={{ fontSize: 10, marginRight: 3 }} />
      {flat ? "—" : `${Math.abs(d).toFixed(0)}%`}
      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6, fontWeight: 400 }}>vs préc.</Text>
    </span>
  );
};

const KpiCard = ({ kpi }) => (
  <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", height: "100%" }}
    bodyStyle={{ padding: 18 }}>
    <Text type="secondary" style={{ fontSize: 12.5 }}>{kpi.label}</Text>
    <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.secondary, margin: "4px 0 6px" }}>
      {fmt(kpi.value, kpi.format)}
    </div>
    <Delta value={kpi.value} previous={kpi.previous} />
  </Card>
);

const HomeDashboard = () => {
  const [periode, setPeriode] = useState("mois");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback((p) => {
    setLoading(true);
    axiosInstance
      .get(`/api/stats/home?periode=${p}`)
      .then((res) => { setData(res.data); setLoading(false); })
      .catch((err) => { console.log(err); setLoading(false); });
  }, []);

  useEffect(() => { fetchData(periode); }, [periode, fetchData]);

  const series = data?.series || [];
  // 2e graphe SINISTRES (Déclarations barres / Décaissements courbe) — fourni au Pool & superadmin.
  const sinistreSeries = data?.sinistreSeries || [];
  const repartition = (data?.repartition || []).filter((s) => s.value > 0);
  const labelGauche = data?.serieGaucheLabel || "Contrats produits";
  const labelDroite = data?.serieDroiteLabel || "Encaissements";
  const titreDonut = data?.repartitionTitre || "Répartition";

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <Text strong style={{ fontSize: 15, color: COLORS.secondary }}>Tableau de bord</Text>
        <Segmented options={PERIODES} value={periode} onChange={setPeriode} />
      </div>

      <Spin spinning={loading}>
        {/* KPIs */}
        <Row gutter={[16, 16]}>
          {(data?.kpis || []).map((kpi) => (
            <Col xs={12} md={6} key={kpi.key}><KpiCard kpi={kpi} /></Col>
          ))}
        </Row>

        {/* Aperçu sinistres (Déclarations + Décaissements) — fourni par le backend au POOL et au
            SUPERADMIN. On l'affiche dès que la donnée est présente (le backend est l'autorité sur qui
            y a droit), sans re-brider sur isPool côté front. */}
        {data?.sinistre && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={12} md={6}>
              <KpiCard kpi={{ key: "sinDecl", label: "Sinistres déclarés", value: data.sinistre.declares, previous: data.sinistre.declaresPrev, format: "number" }} />
            </Col>
            <Col xs={12} md={6}>
              <KpiCard kpi={{ key: "sinDec", label: "Paiements sinistres", value: data.sinistre.decaisse, previous: data.sinistre.decaissePrev, format: "currency" }} />
            </Col>
          </Row>
        )}

        {/* Graphiques */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={15}>
            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
              bodyStyle={{ padding: 18 }}>
              <Text strong style={{ color: COLORS.secondary }}>{labelGauche} & {labelDroite}</Text>
              <div style={{ height: 280, marginTop: 10 }}>
                {series.length === 0 ? (
                  <Empty description="Aucune donnée sur la période" style={{ marginTop: 60 }} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                        tickFormatter={(v) => displayNumbers(Math.trunc(v))} />
                      <Tooltip formatter={(value, name) => name === labelDroite
                        ? [`${displayNumbers(Math.trunc(value))} F`, name] : [displayNumbers(value), name]} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="left" dataKey="production" name={labelGauche} fill="#1890ff" radius={[4, 4, 0, 0]} barSize={18} />
                      <Line yAxisId="right" type="monotone" dataKey="encaisse" name={labelDroite} stroke="#52c41a" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={9}>
            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
              bodyStyle={{ padding: 18 }}>
              <Text strong style={{ color: COLORS.secondary }}>{titreDonut}</Text>
              <div style={{ height: 280, marginTop: 10 }}>
                {repartition.length === 0 ? (
                  <Empty description="Aucune donnée" style={{ marginTop: 60 }} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={repartition} dataKey="value" nameKey="nom" cx="50%" cy="50%"
                        innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {repartition.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => displayNumbers(Math.trunc(v))} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* 2e graphe : évolution SINISTRES (Déclarations & Décaissements) — Pool & superadmin.
            En échelle « Exercice », montre l'évolution mois par mois, comme le graphe de production. */}
        {sinistreSeries.length > 0 && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                bodyStyle={{ padding: 18 }}>
                <Text strong style={{ color: COLORS.secondary }}>Déclarations & Décaissements</Text>
                <div style={{ height: 280, marginTop: 10 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={sinistreSeries} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                        tickFormatter={(v) => displayNumbers(Math.trunc(v))} />
                      <Tooltip formatter={(value, name) => name === "Décaissements"
                        ? [`${displayNumbers(Math.trunc(value))} F`, name] : [displayNumbers(value), name]} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="left" dataKey="production" name="Déclarations" fill="#1890ff" radius={[4, 4, 0, 0]} barSize={18} />
                      <Line yAxisId="right" type="monotone" dataKey="encaisse" name="Décaissements" stroke="#52c41a" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default HomeDashboard;
